import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import type { Showcase } from '@k4ia/shared'
import {
  createNfcTag,
  deleteNfcTag,
  listNfcTags,
  listShowcases,
  type AdminNfcTag,
} from '../lib/api'

/**
 * SPEC „NFC-Konzept": Ein Tag am Eingang startet das Gespräch, ein Tag je
 * Showcase öffnet es direkt am Stand. Auf den Tag kommt nur eine URL — die
 * hier steht; daneben ein QR-Code für Geräte ohne NFC.
 *
 * **Basis-URL:** Sie kommt aus der Umgebung (Staging oder Production), damit
 * hier URLs stehen, die auch erreichbar sind. Solange die Domain noch nicht
 * geschaltet ist, lässt sie sich hier überschreiben (etwa mit der
 * workers.dev-Adresse); das merkt sich der Browser. Tags sind wiederbeschreibbar —
 * wechselt die Domain, werden sie neu beschrieben, nicht neu gekauft.
 */
export function tagUrl(
  tag: AdminNfcTag,
  showcaseSlugById: Map<string, string>,
  baseUrl = 'https://www.k4ia.ai',
): string {
  const base = baseUrl.replace(/\/+$/, '')
  if (tag.target === 'start') {
    return `${base}/start${tag.location ? `?lab=${encodeURIComponent(tag.location)}` : ''}`
  }
  const slug = tag.showcaseId ? (showcaseSlugById.get(tag.showcaseId) ?? tag.showcaseId) : ''
  return `${base}/s/${slug}`
}

const BASIS_URL_SCHLUESSEL = 'k4ia.nfc.baseUrl'

/** Web NFC gibt es nur in Chrome auf Android — dort schreibt das Admin den Tag selbst. */
export function nfcSchreibenMoeglich(): boolean {
  return typeof window !== 'undefined' && 'NDEFReader' in window
}

interface NDEFReaderLike {
  write(message: { records: { recordType: string; data: string }[] }): Promise<void>
}

export function NfcTagsPage() {
  const [tags, setTags] = useState<AdminNfcTag[]>([])
  const [showcases, setShowcases] = useState<Showcase[]>([])
  const [serverBaseUrl, setServerBaseUrl] = useState('')
  const [baseUrlOverride, setBaseUrlOverride] = useState(() => {
    try {
      return localStorage.getItem(BASIS_URL_SCHLUESSEL) ?? ''
    } catch {
      return ''
    }
  })
  const [target, setTarget] = useState<'start' | 'showcase'>('showcase')
  const [showcaseId, setShowcaseId] = useState('')
  const [bezeichnung, setBezeichnung] = useState('')
  const [location, setLocation] = useState('')
  const [meldung, setMeldung] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const baseUrl = baseUrlOverride.trim() || serverBaseUrl

  async function refresh() {
    const [{ nfcTags, baseUrl }, { showcases }] = await Promise.all([
      listNfcTags(),
      listShowcases(),
    ])
    setTags(nfcTags)
    setServerBaseUrl(baseUrl)
    setShowcases(showcases)
  }

  useEffect(() => {
    void refresh()
  }, [])

  useEffect(() => {
    try {
      if (baseUrlOverride.trim()) localStorage.setItem(BASIS_URL_SCHLUESSEL, baseUrlOverride.trim())
      else localStorage.removeItem(BASIS_URL_SCHLUESSEL)
    } catch {
      // Ohne Speicher gilt die Eingabe für diese Sitzung.
    }
  }, [baseUrlOverride])

  const showcaseSlugById = new Map(showcases.map((s) => [s.id, s.slug]))
  const showcaseTitleById = new Map(showcases.map((s) => [s.id, s.title]))
  const live = showcases.filter((s) => s.status === 'live')
  const ohneTag = live.filter((s) => !tags.some((t) => t.showcaseId === s.id))

  async function run(aktion: () => Promise<unknown>) {
    setBusy(true)
    setMeldung(null)
    try {
      await aktion()
      await refresh()
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  function handleCreate() {
    if (target === 'showcase' && !showcaseId) return
    void run(async () => {
      await createNfcTag({
        tagUid: bezeichnung.trim() || undefined,
        target,
        showcaseId: target === 'showcase' ? showcaseId : undefined,
        location: target === 'start' ? location.trim() || undefined : undefined,
      })
      setBezeichnung('')
      setLocation('')
    })
  }

  /** Ein Tag je Live-Showcase, der noch keinen hat — plus Eingang, falls keiner da ist. */
  function handleCreateAll() {
    void run(async () => {
      for (const sc of ohneTag) await createNfcTag({ target: 'showcase', showcaseId: sc.id })
      if (!tags.some((t) => t.target === 'start')) await createNfcTag({ target: 'start' })
    })
  }

  async function handlePrint() {
    const karten = await Promise.all(
      tags.map(async (tag) => ({
        titel: titelVon(tag, showcaseTitleById),
        url: tagUrl(tag, showcaseSlugById, baseUrl),
        qr: await QRCode.toDataURL(tagUrl(tag, showcaseSlugById, baseUrl), {
          width: 600,
          margin: 1,
        }),
      })),
    )
    const fenster = window.open('', '_blank')
    if (!fenster) {
      setMeldung('Der Browser hat das Druckfenster blockiert — Pop-ups für diese Seite erlauben.')
      return
    }
    fenster.document.write(druckbogenHtml(karten))
    fenster.document.close()
    fenster.focus()
    fenster.print()
  }

  return (
    <div className="max-w-3xl space-y-8">
      <div className="space-y-2">
        <h2 className="page-title">NFC-Tags &amp; QR-Codes</h2>
        <p className="text-sm leading-relaxed text-text-muted">
          Ein Tag am Eingang startet das Gespräch, ein Tag je Showcase öffnet es direkt am Stand.
          Auf den Tag kommt die URL unten; der QR-Code ist für Geräte ohne NFC.
        </p>
      </div>

      {meldung && (
        <p role="alert" className="text-sm text-status-error">
          {meldung}
        </p>
      )}

      <div className="card space-y-3 p-5">
        <h3 className="font-medium">Basis-URL</h3>
        <input
          className="input font-mono md:text-xs"
          type="url"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          placeholder={serverBaseUrl || 'https://…'}
          value={baseUrlOverride}
          onChange={(e) => setBaseUrlOverride(e.target.value)}
        />
        <p className="text-xs text-text-muted">
          Vorgabe dieser Umgebung: <span className="font-mono">{serverBaseUrl || '…'}</span>. Ist
          die Domain noch nicht geschaltet, hier die erreichbare Adresse eintragen (z. B. die
          workers.dev-Adresse). Tags lassen sich später neu beschreiben.
        </p>
      </div>

      <div className="card space-y-3 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-medium">Neuer Tag</h3>
          {ohneTag.length > 0 && (
            <button
              type="button"
              onClick={handleCreateAll}
              disabled={busy}
              className="btn-secondary w-full sm:w-auto"
            >
              Für alle {ohneTag.length} Showcases ohne Tag anlegen
            </button>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <select
            className="input"
            value={target}
            onChange={(e) => setTarget(e.target.value as 'start' | 'showcase')}
          >
            <option value="showcase">Showcase (/s/&lt;slug&gt;)</option>
            <option value="start">Eingang (/start)</option>
          </select>
          {target === 'showcase' ? (
            <select
              className="input"
              value={showcaseId}
              onChange={(e) => setShowcaseId(e.target.value)}
            >
              <option value="">Showcase wählen …</option>
              {showcases.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                  {s.status !== 'live' ? ' (nicht live)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="input"
              placeholder="Standort (optional, z. B. pforzheim)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          )}
          <input
            className="input"
            placeholder={'Bezeichnung (optional — sonst z. B. „drohne" oder „eingang")'}
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={busy || (target === 'showcase' && !showcaseId)}
          className="btn-primary w-full sm:w-auto"
        >
          Tag anlegen
        </button>
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="section-title">
            {tags.length} {tags.length === 1 ? 'Tag' : 'Tags'}
          </h3>
          <button
            type="button"
            onClick={() => void handlePrint()}
            className="btn-secondary w-full sm:w-auto"
          >
            Druckbogen mit QR-Codes
          </button>
        </div>
      )}

      <div className="grid gap-3 min-[420px]:grid-cols-2 sm:grid-cols-3 sm:gap-4">
        {tags.map((tag) => (
          <TagCard
            key={tag.id}
            tag={tag}
            titel={titelVon(tag, showcaseTitleById)}
            url={tagUrl(tag, showcaseSlugById, baseUrl)}
            onDelete={() => {
              if (window.confirm(`Tag „${tag.tagUid}" löschen?`)) {
                void run(() => deleteNfcTag(tag.id))
              }
            }}
            onMeldung={setMeldung}
          />
        ))}
      </div>

      <div className="card space-y-2 p-5 text-sm leading-relaxed text-text-muted">
        <h3 className="font-medium text-text">So kommt die URL auf den Tag</h3>
        <p>
          <strong className="text-text">Android (Chrome):</strong> Beim Tag auf „Auf Tag schreiben"
          tippen und den leeren Tag ans Handy halten.
        </p>
        <p>
          <strong className="text-text">iPhone:</strong> App „NFC Tools" (kostenlos) → Schreiben →
          Datensatz hinzufügen → URL → die URL aus der Karte einfügen → Schreiben → Tag ans Handy
          halten.
        </p>
        <p>
          Geeignet sind NTAG213 oder NTAG215 als Aufkleber oder Karte. Ein Tag pro Stand auf
          Hüfthöhe, dazu der QR-Code aus dem Druckbogen daneben.
        </p>
      </div>
    </div>
  )
}

function titelVon(tag: AdminNfcTag, titles: Map<string, string>): string {
  if (tag.target === 'start') return tag.location ? `Eingang ${tag.location}` : 'Eingang'
  return (tag.showcaseId && titles.get(tag.showcaseId)) || tag.tagUid
}

function TagCard({
  tag,
  titel,
  url,
  onDelete,
  onMeldung,
}: {
  tag: AdminNfcTag
  titel: string
  url: string
  onDelete: () => void
  onMeldung: (text: string | null) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [schreibt, setSchreibt] = useState(false)
  const [geschrieben, setGeschrieben] = useState(false)
  const [kopiert, setKopiert] = useState(false)

  useEffect(() => {
    if (canvasRef.current) void QRCode.toCanvas(canvasRef.current, url, { width: 160, margin: 1 })
  }, [url])

  async function schreiben() {
    setSchreibt(true)
    setGeschrieben(false)
    onMeldung(null)
    try {
      const Reader = (window as unknown as { NDEFReader: new () => NDEFReaderLike }).NDEFReader
      await new Reader().write({ records: [{ recordType: 'url', data: url }] })
      setGeschrieben(true)
    } catch (err) {
      onMeldung(
        `Schreiben fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}. Tag ruhig ans Handy halten und noch einmal versuchen.`,
      )
    } finally {
      setSchreibt(false)
    }
  }

  return (
    <div className="card flex gap-4 p-4 text-left text-xs min-[420px]:block min-[420px]:p-3 min-[420px]:text-center">
      <canvas
        ref={canvasRef}
        className="h-28 w-28 shrink-0 rounded-lg bg-white min-[420px]:mx-auto min-[420px]:mb-2 min-[420px]:h-auto min-[420px]:w-auto"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text">{titel}</p>
        <p className="font-mono text-text-muted">{tag.tagUid}</p>
        <p className="mt-1 break-all text-text-muted">{url}</p>
        <div className="mt-2 flex flex-wrap gap-2 min-[420px]:justify-center">
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(url)
              setKopiert(true)
              setTimeout(() => setKopiert(false), 1500)
            }}
            className="btn-chip"
          >
            {kopiert ? '✓ Kopiert' : 'URL kopieren'}
          </button>
          {nfcSchreibenMoeglich() && (
            <button
              type="button"
              onClick={() => void schreiben()}
              disabled={schreibt}
              className="btn-chip"
            >
              {schreibt
                ? 'Tag ans Handy halten …'
                : geschrieben
                  ? '✓ geschrieben'
                  : 'Auf Tag schreiben'}
            </button>
          )}
          <button type="button" onClick={onDelete} className="btn-chip-danger">
            Löschen
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Ein Bogen zum Ausdrucken: sechs Karten je A4-Seite, jede mit Titel, QR-Code
 * und URL. Eigenes Fenster statt Druck-CSS im Admin — so hängt der Ausdruck
 * nicht an der Navigation und dem Layout der App.
 */
export function druckbogenHtml(karten: { titel: string; url: string; qr: string }[]): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  const items = karten
    .map(
      (k) => `<section class="karte">
  <h2>${escape(k.titel)}</h2>
  <img src="${k.qr}" alt="QR-Code für ${escape(k.titel)}" />
  <p class="hinweis">Handy auflegen oder Code scannen</p>
  <p class="url">${escape(k.url)}</p>
</section>`,
    )
    .join('\n')
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8" /><title>K4ia – NFC-Tags und QR-Codes</title>
<style>
  @page { size: A4; margin: 12mm; }
  body { font-family: -apple-system, Helvetica, Arial, sans-serif; color: #1d1d1f; margin: 0; }
  .bogen { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8mm; }
  .karte { border: 1px dashed #999; border-radius: 4mm; padding: 6mm; text-align: center; break-inside: avoid; }
  .karte h2 { font-size: 16pt; margin: 0 0 4mm; }
  .karte img { width: 55mm; height: 55mm; }
  .hinweis { font-size: 10pt; margin: 3mm 0 1mm; }
  .url { font-family: ui-monospace, Menlo, monospace; font-size: 8pt; color: #555; word-break: break-all; margin: 0; }
</style></head>
<body><div class="bogen">
${items}
</div></body></html>`
}
